import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { Check, Cancel } from "@mui/icons-material";
import { Button, CircularProgress } from "@deere/fuel-react";
import { Tooltip } from "@mui/material";
import ToastMessageFunction from "../../common/toast-message-function";
import {
  getAccountingApprovalTransactions,
  approveAccountingTransaction,
  rejectAccountingTransaction,
  // getMaterialTypes,
} from "./utils/api";
import { useSelector } from "react-redux";
import TransactionsTable from "../common/TransactionsTable";
import InvoiceNumberCell from "../common/InvoiceNumberCell";
import RejectReasonModal from "./RejectReasonModal";
import SearchFilters from "./components/SearchFilters";

const AccountingApproval = () => {
  const user = useSelector((state) => state.userState.userData);

  const isAccountingMember = user?.authorities?.includes("U4NBNACC") ?? false;
  
  const isBNAdmin = user?.userRole === "BN_ADMIN";
  
  const canPerformActions = isAccountingMember && !isBNAdmin;
  const toastManager = ToastMessageFunction();
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingRejectRows, setPendingRejectRows] = useState([]);
  // const [materialTypes, setMaterialTypes] = useState([]);
  const [allTransactionMaterials, setAllTransactionMaterials] = useState([]);
  const [filters, setFilters] = useState({ materialType: [], approvalLevel: null, taxDepartureCountry: null });

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const fetchTransactions = useCallback(async (filterParams = {}) => {
    setIsLoading(true);
    try {
      const mergedFilters = { ...filters, ...filterParams };
      const response = await getAccountingApprovalTransactions(user, mergedFilters);

      if (response && response.success) {
        const mappedData = response.data.map((item) => {
          let memoRequest = null;
          let requestItems = [];
          let amount = 0;
          let currency = "";
          let material = "";

          // Determine if the item is a debit or credit memo request
          if (item.debitMemoRequest) {
            memoRequest = { ...item.debitMemoRequest };
            requestItems = memoRequest.debitMemoRequestItem || [];
          } else if (item.creditMemoRequest) {
            memoRequest = { ...item.creditMemoRequest };
            requestItems = memoRequest.creditMemoRequestItem || [];
          }

          // Extract amount, currency, material, and quantity from request items
          let targetQty = 0;
          if (requestItems.length > 0) {
            const conditions =
              requestItems[0][
                item.debitMemoRequest
                  ? "debitMemoRequestItemConditions"
                  : "creditMemoRequestItemConditions"
              ] || [];
            amount = conditions.length > 0 ? conditions[0]?.condValue || 0 : 0;
            currency = conditions.length > 0 ? conditions[0]?.currency || "" : "";
            material = requestItems[0]?.material || "";
            targetQty = requestItems[0]?.targetQty || 0;
          }

          const itemTextString =
            requestItems.length > 0
              ? requestItems[0].itemText.join("\r\n")
              : "";

          return {
            ...memoRequest,
            billingDataId: item.billingDataId,
            source: item.billingDataId ? "UPLOAD" : "API",
            dealerNumber: item.dealerNumber,
            dealerUnit: item.dealerUnit,
            dealerName: item.dealerName,
            amount: amount, // Amount per unit from condition
            targetQty: targetQty, // Quantity from request items
            totalAmount:
              item.totalAmount ||
              (amount && targetQty > 0 ? amount * targetQty : 0),
            currencydisplay: currency,
            material: material,
            itemText: itemTextString,
            transactionType: item.debitMemoRequest ? "Debit" : "Credit",
            isSapNumberUploaded: item.sapNumberUploaded,
            sapNumber: item.sapNumber,
            // User who created/uploaded the transaction
            userId: item.userId || "", // Get from item (backend response), not memoRequest
            // Accounting-specific fields
            conversionRate: item.conversionRate,
            convertedAmountInEur: item.convertedAmountInEur, // Converted total amount in EUR
            accountingStatus: item.accountingStatus,
            reasonOfRejection: item.reasonOfRejection,
          };
        });

        setFilteredTransactions(mappedData);

        // Populate distinct material types only from the unfiltered (initial/clear) fetch
        const hasMaterialFilter = Array.isArray(mergedFilters.materialType) ? mergedFilters.materialType.length > 0 : !!mergedFilters.materialType;
        const isUnfiltered = !hasMaterialFilter && !mergedFilters.approvalLevel && !mergedFilters.taxDepartureCountry;
        if (isUnfiltered) {
          const distinctMaterials = [...new Set(mappedData.map((t) => t.material).filter(Boolean))].sort();
          setAllTransactionMaterials(distinctMaterials);
        }
      } else {
        showToast(
          response.message || "Failed to fetch accounting transactions",
          "error"
        );
      }
    } catch (error) {
      showToast(error.message || "An error occurred", "error");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // useEffect(() => {
  //   const fetchMaterialTypes = async () => {
  //     try {
  //       const response = await getMaterialTypes();
  //       if (Array.isArray(response)) {
  //         setMaterialTypes(response);
  //       } else if (response && response.success) {
  //         setMaterialTypes(response.data || []);
  //       }
  //     } catch (error) {
  //       // Silently fail — dropdown will just be empty
  //     }
  //   };
  //   fetchMaterialTypes();
  // }, []);

  const handleSearch = (searchFilters) => {
    setFilters(searchFilters);
    fetchTransactions(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = { materialType: null, approvalLevel: null, taxDepartureCountry: null };
    setFilters(clearedFilters);
    fetchTransactions(clearedFilters);
  };

  // Distinct material types derived from the initial unfiltered transaction data
  // This remains stable even when user applies filters
  const filteredMaterialTypes = allTransactionMaterials;

  const handleApproveClick = async (rows) => {
    const selectedRows = Array.isArray(rows) ? rows : [rows];
    const billingDataIds = selectedRows
      .map((row) => parseInt(filteredTransactions[row.index].billingDataId, 10))
      .filter((id) => Number.isInteger(id));

    try {
      setIsLoading(true);
      const response = await approveAccountingTransaction(billingDataIds);
      if (response.success) {
        showToast(response.message, "success");
      } else {
        showToast(`Approval failed: ${response.message}`, "error");
      }
    } catch (error) {
      showToast(`An error occurred: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
       fetchTransactions(filters);
    }
  };

  const handleRejectClick = (rows) => {
    setPendingRejectRows(Array.isArray(rows) ? rows : [rows]);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    const billingDataIds = pendingRejectRows
      .map((row) => parseInt(filteredTransactions[row.index].billingDataId, 10))
      .filter((id) => Number.isInteger(id));

    setRejectModalOpen(false);
    try {
      setIsLoading(true);
      const response = await rejectAccountingTransaction(billingDataIds, rejectReason);
      if (response.success) {
        showToast(response.message, "success");
      } else {
        showToast(`Rejection failed: ${response.message}`, "error");
      }
    } catch (error) {
      showToast(`An error occurred: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
       fetchTransactions(filters);
    }
  };

  // Define columns for the accounting approval table
  // Columns: Action, Billing Date, Dealer, Sap AccountNo, Material, Ord Type, Item Note, Quantity, Amount, Currency, Total Amount in €, Invoice Number, User

    const actionColumn = {
      id: "action",
      header: "Action",
      size: 160,
      enableColumnPinning: true,
      enableColumnActions: true,
      columnDefType: 'display',
      Cell: ({ row }) => {
        const { accountingStatus } = row.original;
        const isApproved = accountingStatus === "APPROVED";
        const isRejected = accountingStatus === "REJECTED";
        return (
          <Box>
            <Tooltip title={canPerformActions ? "Approve Transaction" : "View Only (Actions disabled for BN_ADMIN)"}>
              <span>
                <Button
                  variant="contained"
                  size="small" 
                  style={{ marginRight: "8px" }}
                  color="success"
                  onClick={() => handleApproveClick(row)}
                  disabled={isApproved || isRejected || !canPerformActions}
                >
                  <Check />
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={canPerformActions ? "Reject Transaction" : "View Only (Actions disabled for BN_ADMIN)"}>
              <span>
                <Button
                  variant="contained"
                  size="small" 
                  color="error"
                  onClick={() => handleRejectClick(row)}
                  disabled={isApproved || isRejected || !canPerformActions}
                >
                  <Cancel />
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      },
    };
  const columns = [
      ...(canPerformActions ? [actionColumn] : []),
    {
      header: "Billing Date",
      accessorKey: "billingDate",
      size: 120,
      Cell: ({ row }) => {
        const billingDate = row.original.billingDate;
        return billingDate
          ? new Date(billingDate).toLocaleDateString("en-GB")
          : "";
      },
    },
    {
            header: "Dealer Number",
      accessorKey: "dealerNumber",
      size: 100,
      Cell: ({ row }) => {
        const { dealerName, dealerNumber } = row.original;
        return dealerName ? (
          <Tooltip title={dealerName}>
            <span style={{ cursor: "default" }}>{dealerNumber}</span>
          </Tooltip>
        ) : (
          <span>{dealerNumber}</span>
        );
      },
    },
    {
      header: "Sap AccountNo",
      accessorKey: "soldTo",
      size: 130,
    },
    {
      header: "Material",
      accessorKey: "material",
      size: 120,
    },
    {
      header: "Ord Type",
      accessorKey: "transactionType",
      size: 100,
    },
    {
      header: "Item Note",
      accessorKey: "itemText",
      size: 200,
      Cell: ({ row }) => {
        const itemText = row.original.itemText;
        return (
          <Box
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px",
            }}
            title={itemText}
          >
            {itemText}
          </Box>
        );
      },
    },
    {
      header: "Quantity",
      accessorKey: "targetQty",
      size: 100,
      muiTableBodyCellProps: {
        sx: { textAlign: 'center', borderBottom: '1px solid rgb(224, 224, 224)' }
      },
      muiTableHeadCellProps: {
        sx: { textAlign: 'center' }
      },
    },
    {
      header: "Amount",
      accessorKey: "amount",
      size: 90,
      muiTableBodyCellProps: {
        sx: { 
          textAlign: 'right',
          paddingLeft: 0,
          paddingRight: 0,
          borderBottom: '1px solid rgb(224, 224, 224)'
        }
      },
      muiTableHeadCellProps: {
        sx: { textAlign: 'right', paddingLeft: 0, paddingRight: 0 }
      },
      Cell: ({ row }) => {
        const amount = row.original.amount;
        const formatted = amount ? amount.toFixed(2) : "0.00";
        return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
      },
    },
    {
      header: "Currency",
      accessorKey: "currencydisplay",
      size: 100,
      muiTableBodyCellProps: {
        sx: { textAlign: 'center', borderBottom: '1px solid rgb(224, 224, 224)' }
      },
      muiTableHeadCellProps: {
        sx: { textAlign: 'center' }
      },
    },
    {
      header: "Total Amount in €",
      accessorKey: "convertedAmountInEur",
      size: 70,
      maxSize: 70,
      muiTableBodyCellProps: {
        sx: { 
          textAlign: 'right',
          paddingLeft: 0,
          paddingRight: 0,
          borderBottom: '1px solid rgb(224, 224, 224)'
        }
      },
      muiTableHeadCellProps: {
        sx: { 
          textAlign: 'right',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          lineHeight: 1.2
        }
      },
      Cell: ({ row }) => {
        const convertedAmount = row.original.convertedAmountInEur;
        // Only display if converted amount exists, otherwise show blank
        if (!convertedAmount) return "";
        const formatted = convertedAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
        return `€\u00A0${formatted}`;
      },
    },
    {
      header: "Invoice Number",
      accessorKey: "invoiceNumber",
      size: 150,
      Cell: ({ row }) => {
        const { invoiceNumber, requestNumber, accountNumber, source } = row.original;
        return (
          <InvoiceNumberCell
            invoiceNumber={invoiceNumber}
            requestNumber={requestNumber}
            accountNumber={accountNumber}
            source={source}
          />
        );
      },
    },
    {
      header: "User",
      accessorKey: "userId",
      size: 90,
    },
  ];

  return (
    <Box>
      {toastManager.toast}

      {/* Search Filters - Outside loading conditional to maintain state */}
      <SearchFilters
        onSearch={handleSearch}
        onClear={handleClearFilters}
        materialTypes={filteredMaterialTypes}
      />

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress aria-label={"Loading..."} data-fuel-size={"medium"} />
        </Box>
      ) : (
        <div>
          {/* Transactions Table - All records loaded without pagination */}
          <TransactionsTable
            columns={columns}
            filteredTransactions={filteredTransactions}
            fetchTransactions={fetchTransactions}
            showToast={showToast}
            disableRowExpansion={true}
            initialColumnPinning={canPerformActions ? { left: ["mrt-row-select", "action"] } : { left: ["mrt-row-select"] }}
            handleApproveClick={canPerformActions ? handleApproveClick : undefined}
            handleRejectClick={canPerformActions ? handleRejectClick : undefined}
          />
        </div>
      )}
      <RejectReasonModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        reason={rejectReason}
        onReasonChange={(e) => setRejectReason(e.target.value)}
        isBatch={pendingRejectRows.length > 1}
      />
    </Box>
  );
};

export default AccountingApproval;
